import {CONFIG} from '../../appConfig';
import STATE from '../../constants/state';
import {types} from '../../utils/js/types';

const TRIPS = 'trips/';
const PAST = 'past';

export default {
  /**
   * @desc Get current trip
   * @returns {Object} data {params}
   * @memberof services.gma.trips
   * @example
   * services.gma.trips.current();
   * // dispatched results stored in `state.gmi.gma.trips.current`
   */
  current(data = {}){
    const {params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${TRIPS}current`,
      statePath: STATE.GMA.TRIPS.CURRENT,
      params
    };
  },

  /**
   * @desc Get past trip details invoice by invoiceNumber and lastName
   * @param {Object} data // { params: {invoiceNumber, lastName} }
   * @returns {Object}
   * @memberof services.gma.trips
   * @example
   * services.gma.trips.invoice({params: {invoiceNumber: '1234', lastName: 'Tester'}});
   * // dispatched results stored in `state.gmi.gma.trips.invoice['1234']`
   */
  invoice(data = {}){
    const {params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${TRIPS}invoice`,
      params,
      statePath: STATE.GMA.TRIPS.INVOICE.concat([params.invoiceNumber])
    };
  },

  /**
   * @desc Get past trips for auth or aunauth user; auth must still supply membership_number in post data and unauth must supply last_name and driver_license
   * @param {Object} data // {payload: { [membership_number][, last_name][, driver_license][, from_date][, to_date] }, params}
   * @returns {Object}
   * @memberof services.gma.trips
   * @example
   * // make a call WITH defined from_date and to_date
   * const payload = {from_date: '1234', to_date: '5678', membership_number: '31232123'}
   * services.gma.trips.past({payload});
   * // dispatched results stored in `state.gmi.gma.trips.past['1234|5678']`
   *
   * // make a call WITHOUT defined from_date and to_date
   * const payload = {membership_number: '31232123'}
   * services.gma.trips.past({payload});
   * // dispatched results stored in `state.gmi.gma.trips.past`
   *
   * // make a unauth call WITHOUT defined from_date and to_date
   * const payload = {last_name: 'Tester', driver_license: '312312312'}
   * services.gma.trips.past({payload});
   * // dispatched results stored in `state.gmi.gma.trips.past`
   */
  past(data = {}){
    const {payload = {}, params = {}} = data;
    const {from_date, to_date} = payload;
    const fromTypes = types(from_date);
    const toTypes = types(to_date);
    let concatPath = fromTypes.isDefined ? from_date : '';
    if (fromTypes.isDefined || toTypes.isDefined) concatPath = `${concatPath}|`;
    if (toTypes.isDefined) concatPath = `${concatPath}${to_date}`;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${TRIPS}${PAST}`,
      data: payload,
      params,
      statePath: concatPath.length === 0 ? STATE.GMA.TRIPS.PAST : STATE.GMA.TRIPS.PAST.concat([concatPath])
    };
  },

  /**
   * @desc Get past trips for auth or aunauth user and retain in session; auth must still supply membership_number in post data and unauth must supply last_name and driver_license
   * @param {Object} data // {payload: { [membership_number][, last_name][, driver_license][, from_date][, to_date] }, params}
   * @returns {Object}
   * @memberof services.gma.trips
   * @example
   * // make a call WITH defined from_date and to_date
   * const payload = {from_date: '1234', to_date: '5678', membership_number: '31232123'}
   * services.gma.trips.pastRetain({payload});
   * // dispatched results stored in `state.gmi.session.gbo.past_trips`
   *
   * // make a unauth call WITHOUT defined from_date and to_date
   * const payload = {last_name: 'Tester', driver_license: '312312312'}
   * services.gma.trips.pastRetain({payload});
   * // dispatched results stored in `state.gmi.session.gbo.past_trips`
   */
  pastRetain(data = {}){
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${TRIPS}${PAST}/retain`,
      data: payload,
      params
    };
  },

  /**
   * @desc Get trips for any type of user (auth or unauth); uses reservations/retrieve behind the scenes
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.trips
   * @example
   * const payload = {
   *  trips: [
   *    {first_name: 'John', last_name: 'Doe', confirmation_number: '1206669885'},
   *    {first_name: 'Jane', last_name: 'Doe', confirmation_number: '1600001965'}
   *  ]
   * };
   * services.gma.trips.retrieve({payload});
   * // dispatched results stored in `state.gmi.gma.trips.retrieve`
   */
  retrieve(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${TRIPS}retrieve`,
      data: payload,
      params,
      statePath: STATE.GMA.TRIPS.RETRIEVE
    };
  },

  /**
   * @desc Get upcoming trips for logged in user
   * @param {Object} [data] {params: {startIndex, quantity}}
   * @returns {Object}
   * @memberof services.gma.trips
   * @example
   * // make a call WITH defined startRecordNumber and recordCount
   * services.gma.trips.upcoming({startRecordNumber: 1, recordCount: 10});
   * // dispatched results stored in `state.gmi.gma.trips.upcoming['1|10']`
   *
   * // make a call WITHOUT defined startRecordNumber and recordCount
   * services.gma.trips.upcoming();
   * // dispatched results stored in `state.gmi.gma.trips.upcoming`
   */
  upcoming(data = {}){
    const {params = {}} = data;
    const {startRecordNumber, recordCount} = params;
    const startTypes = types(startRecordNumber);
    const recordTypes = types(recordCount);
    let concatPath = startTypes.isDefined ? startRecordNumber : '';
    if (startTypes.isDefined || recordTypes.isDefined) concatPath = `${concatPath}|`;
    if (recordTypes.isDefined) concatPath = `${concatPath}${recordCount}`;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${TRIPS}upcoming`,
      params,
      statePath: concatPath.length === 0 ? STATE.GMA.TRIPS.UPCOMING : STATE.GMA.TRIPS.UPCOMING.concat([concatPath])
    };
  }
};
